"use client";

import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldControl } from "@/components/ui/field";
import { syncExtensionUserId } from "@/lib/extension-sync";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, LogIn, Mail, UserPlus } from "lucide-react";

function LoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);

  // Handle OAuth redirect callback
  useEffect(() => {
    const userId = searchParams?.get("userId");
    const provider = searchParams?.get("provider");
    const token = searchParams?.get("token");
    if (userId && provider === "google") {
      localStorage.setItem("airwriteUserId", userId);
      syncExtensionUserId(userId, { refreshSettings: true, authToken: token ?? undefined });
      toast.success("Signed in with Google");
      router.replace("/dashboard");
    }
  }, [searchParams, router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = isSignUp ? "/api/auth/signup" : "/api/auth/login";
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const rawBody = await response.text();
      let data: {
        user?: { id: string };
        token?: string;
        error?: string;
        message?: string;
        needsVerification?: boolean;
        email?: string;
      } | null = null;
      if (rawBody) {
        try {
          data = JSON.parse(rawBody);
        } catch (error) {
          console.warn("Failed to parse auth response", error);
        }
      }

      if (!response.ok) {
        // Handle unverified email during login
        if (!isSignUp && data?.needsVerification) {
          toast.info("Please verify your email first");
          router.push(`/verify-email?email=${encodeURIComponent(data.email || email)}`);
          return;
        }

        const fallbackMessage = isSignUp
          ? "Failed to create account"
          : response.status === 401
          ? "Invalid email or password"
          : "Authentication failed";
        const message = data?.error || data?.message || fallbackMessage;
        throw new Error(message);
      }

      // Handle signup - redirect to verification page
      if (isSignUp && data?.needsVerification) {
        toast.success("Please check your email to verify your account");
        router.push(`/verify-email?email=${encodeURIComponent(data.email || email)}`);
        return;
      }

      if (!data?.user?.id) {
        throw new Error("Unexpected response from server");
      }

      // Store user ID and notify the extension so it can sync immediately
      localStorage.setItem("airwriteUserId", data.user.id);
      syncExtensionUserId(data.user.id, { refreshSettings: true, authToken: data.token });

      toast.success("Signed in successfully");
      router.push("/dashboard");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      toast.error("Enter your email address first");
      return;
    }

    setIsResetLoading(true);
    try {
      const response = await fetch("/api/auth/forgot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || "Unable to send reset email");
      }

      toast.success("Password reset link sent. Check your inbox.");
      if (data?.devToken && process.env.NODE_ENV !== "production") {
        toast(`Development reset token: ${data.devToken}`);
        console.info("Development reset token:", data.devToken);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to send reset email");
    } finally {
      setIsResetLoading(false);
    }
  };

  return (
    <main
      className="relative flex min-h-screen w-full items-center justify-center bg-[#e6f2ff] bg-cover bg-center bg-no-repeat px-4 py-7"
      style={{ backgroundImage: "url('/auth2.png')" }}
    >
      <div className="absolute left-10 top-6 flex items-center gap-2 text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.25)]">
        <Image src="/logo.png" alt="AirWrite" width={32} height={32} />
        <span className="text-lg font-bold uppercase tracking-wide">AirWrite</span>
      </div>
      <div className="relative w-full max-w-sm overflow-hidden rounded-[18px] border border-gray-200 bg-gradient-to-b from-sky-100/60 via-white to-white px-8 py-7 shadow-2xl shadow-black/50">
        <div
          className="pointer-events-none absolute inset-0 z-0 opacity-70"
          style={{
            backgroundImage: `
              repeating-linear-gradient(22.5deg, transparent, transparent 3px, rgba(75, 85, 99, 0.04) 3px, rgba(75, 85, 99, 0.04) 4px, transparent 4px, transparent 10px),
              repeating-linear-gradient(67.5deg, transparent, transparent 3px, rgba(107, 114, 128, 0.035) 3px, rgba(107, 114, 128, 0.035) 4px, transparent 4px, transparent 10px),
              repeating-linear-gradient(112.5deg, transparent, transparent 3px, rgba(55, 65, 81, 0.03) 3px, rgba(55, 65, 81, 0.03) 4px, transparent 4px, transparent 10px),
              repeating-linear-gradient(157.5deg, transparent, transparent 3px, rgba(31, 41, 55, 0.025) 3px, rgba(31, 41, 55, 0.025) 4px, transparent 4px, transparent 10px)
            `,
          }}
        />
        <div className="relative z-10">
          <div className="mb-5 flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-[18px] bg-white shadow-xl">
              {isSignUp ? (
                <UserPlus className="h-5 w-5 text-gray-700" />
              ) : (
                <LogIn className="h-5 w-5 text-gray-700" />
              )}
            </div>
          </div>
        <div className="space-y-3 text-center">
          <div className="space-y-1">
            <h1 className="text-[18px] font-semibold text-gray-900">
              {isSignUp ? "Create your account" : "Sign in with email"}
            </h1>
            <p className="text-[12.5px] leading-5 text-gray-500">
              Create, rewrite, and polish content with AirWrite.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 text-left">
            <Field>
              <FieldControl className="rounded-xl bg-gray-100 px-3 py-1 text-gray-500 transition hover:ring-1 hover:ring-gray-300">
                <img src="/icons/email.svg" alt="Email" className="h-4 w-4 opacity-60" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-6 border-0 bg-transparent p-0 text-xs leading-4 text-gray-700 placeholder:text-xs placeholder:text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </FieldControl>
            </Field>

            <Field className="space-y-1.5">
              <FieldControl className="rounded-xl bg-gray-100 px-3 py-1 text-gray-500 transition hover:ring-1 hover:ring-gray-300">
                <img src="/icons/password.svg" alt="Password" className="h-4 w-4 opacity-80" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="h-6 flex-1 border-0 bg-transparent p-0 text-xs leading-4 text-gray-700 placeholder:text-xs placeholder:text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="text-gray-500 transition hover:text-gray-700"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  <img
                    src={showPassword ? "/icons/password-unlocked.svg" : "/icons/password-locked.svg"}
                    alt="Toggle password visibility"
                    className="h-4 w-4 opacity-80"
                  />
                </button>
              </FieldControl>
              {!isSignUp && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handlePasswordReset}
                    disabled={isResetLoading}
                    className="text-[12px] font-medium text-gray-600 hover:text-gray-800 disabled:pointer-events-none disabled:opacity-60"
                  >
                    {isResetLoading ? "Sending..." : "Forgot password?"}
                  </button>
                </div>
              )}
            </Field>

            <Button
              type="submit"
              className="h-10 w-full rounded-xl bg-gradient-to-b from-gray-900 to-gray-800 text-xs font-semibold text-white shadow transition hover:from-gray-800 hover:to-gray-700"
              disabled={isLoading}
            >
              {isLoading
                ? isSignUp
                  ? "Creating account..."
                  : "Signing in..."
                : isSignUp
                ? "Create Account"
                : "Get Started"}
            </Button>
          </form>

          <div className="flex items-center justify-center gap-1 text-[12px] text-gray-600">
            <span>{isSignUp ? "Already have an account?" : "Don't have an account?"}</span>
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="font-semibold text-gray-900 hover:text-gray-700"
            >
              {isSignUp ? "Sign in" : "Sign up"}
            </button>
          </div>

          <div className="flex items-center gap-3 text-[12px] text-gray-500">
            <span className="h-px flex-1 bg-gray-200/70" />
            <span>Or sign in with</span>
            <span className="h-px flex-1 bg-gray-200/70" />
          </div>

          <Button
            type="button"
            variant="outline"
            className="h-11 w-full rounded-xl border border-gray-200 bg-white shadow-md shadow-black/10 hover:bg-white"
            onClick={() => (window.location.href = "/api/auth/google")}
          >
            <div className="flex items-center justify-center gap-2">
              <Image src="/Google%20Icon.svg" alt="Google" width={18} height={18} />
              <span className="text-xs font-medium text-gray-700">Continue with Google</span>
            </div>
          </Button>
        </div>
        <div className="mt-6 flex w-full items-center justify-center text-[11px] text-gray-600">
          <div className="flex items-center gap-3">
            <a href="/legal/privacy" className="hover:text-gray-800">Privacy</a>
            <span className="h-3 w-px bg-gray-300" />
            <a href="/legal/terms" className="hover:text-gray-800">Terms</a>
          </div>
        </div>
        </div>
      </div>
    </main>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <main className="flex min-h-screen items-center justify-center bg-[#e6f2ff] px-4 py-10 text-sm text-gray-500">
          Loading sign-in…
        </main>
      }
    >
      <LoginContent />
    </Suspense>
  );
}
